import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $getRoot, $insertNodes, $setSelection, createEditor } from 'lexical';
import { ListItemNode, ListNode } from '@lexical/list';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { useEffect, useId, useState } from 'react';
import { clsx } from 'clsx';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';

import type { RefObject } from 'react';
import type { EditorState, LexicalEditor } from 'lexical';
import type { SearchResponse } from './Plugins/CourseCataloguePlugin/AddCatalogueLinkForm';

import { exportMap, mergeBoldStrongTags, cleanUpTable } from './helpers';
import { MATCHERS, validateUrl } from './Plugins/LinkPlugin/helpers';
import styles from './RteEditor.module.css';
import { ToolBarPlugin, PasteHandlerPlugin, TableCellResizerPlugin } from './Plugins';
import { UvaTheme } from './UvaTheme';
import { EditorHeader } from './EditorHeader';
import { LanguageProvider, useLanguage } from './Providers/LanguageProvider';
import { useTranslation } from './hooks/useTranslation';
import { TableWrapperNode, CourseCatalogueNode, CourseCatalogueLinkPlugin } from './nodes';

import { FeedbackBox } from 'components/FeedbackBox';

export interface RteEditorProps {
  /** current language setting for the editor labels NOT title ans label */
  currentLanguage?: 'NL' | 'EN' | 'nl' | 'en';
  /** unique id mandatory for the rte */
  editorId: string;
  /** imported content to edit */
  initialValue?: string;
  /** placeholder text in current Language */
  placeholder?: string;
  /** label/title of the RTE in current Language */
  label?: string;
  /** explanation of the text to be filled in, in current Language */
  info?: string;
  /** 'true' the rte field mandatory */
  required?: boolean;
  /** `true` to color border red to show not valid; otherwise, `false`. */
  notValid?: boolean;
  /** error text to display if required and field is empty */
  notValidatedText?: string;
  /** max height in rem default = 20rem*/
  maxHeightInRem?: `${number}rem`;
  /** max number of words 'allowed' but not enforced */
  maxWords?: number;
  /** Ref to the Lexical editor instance */
  rteRef?: RefObject<LexicalEditor> | null;
  /** updated output */
  onChange: (content: string) => void;
  /** callback for catalogue link search */
  onCatalogueLinkSearch?: (query: string, type: 'course' | 'programme') => void;
  /** updated catalogue search results */
  catalogueSearchResponse?: SearchResponse;
}

export const Rte = ({
  currentLanguage = 'NL',
  editorId,
  initialValue = '',
  placeholder = '',
  label = '',
  info = '',
  required = false,
  notValid = false,
  notValidatedText = '',
  maxHeightInRem = '20rem',
  maxWords,
  rteRef = null,
  onChange,
  onCatalogueLinkSearch,
  catalogueSearchResponse = { state: 'idle', results: [] },
}: RteEditorProps) => {
  const { switchLanguage } = useLanguage();
  const t = useTranslation();
  const [editor] = useState(() => createEditor());
  const [wordCount, setWordCount] = useState(0);
  const [scrollTopPosition, setScrollTopPosition] = useState(0);
  const rteId = useId();

  const initialConfig = {
    html: {
      export: exportMap,
    },
    namespace: `UvaRte-${editorId.replace(/\s+/g, '-').toLowerCase()}`,
    editor,
    theme: UvaTheme,
    onError(error: Error) {
      throw error;
    },
    nodes: [
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      TableCellNode,
      TableRowNode,
      TableNode,
      TableWrapperNode,
      CourseCatalogueNode,
    ],
    editorState: (editor: LexicalEditor) => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(initialValue, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      $insertNodes(nodes);
      $setSelection(null);
    },
  };

  const getWordCount = (plainText: string): number => {
    if (plainText.trim().length === 0) {
      return 0;
    } else {
      return plainText.split(' ').length;
    }
  };

  const handleOnChange = (_editorState: EditorState, editor: LexicalEditor): void => {
    editor.read(() => {
      let htmlContent = '';
      setWordCount(getWordCount($getRoot().getTextContent().replace(/\s+/g, ' ').trim()));
      htmlContent = mergeBoldStrongTags($generateHtmlFromNodes(editor));
      htmlContent = cleanUpTable(htmlContent);
      onChange(htmlContent);
    });
  };

  useEffect(() => {
    switchLanguage(currentLanguage.toUpperCase() as 'NL' | 'EN');
  }, [currentLanguage, switchLanguage]);

  useEffect(() => {
    const strippedInitialValue = initialValue
      .replace(/<br\s*\/?>|&nbsp;/gi, ' ')
      .replace(/<\/?[^>]+(>|$)|&[a-z]+;/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    setWordCount(getWordCount(strippedInitialValue));
  }, [initialValue]);

  return (
    <div className={styles['editor__container']}>
      {(label || info || maxWords) && (
        <div className={styles['editor__header']}>
          <EditorHeader label={label} info={info} required={required} />
          {maxWords && maxWords > 0 && (
            <div className={styles['editor__header-word-count']}>
              <span className={clsx({ [styles['editor__header-word-count--alert']]: wordCount > maxWords })}>
                {wordCount}
              </span>
              &nbsp;/&nbsp;{maxWords}
            </div>
          )}
        </div>
      )}
      <div
        className={clsx(styles['editor'], {
          [styles['not-valid']]: notValid,
        })}
      >
        <LexicalComposer initialConfig={initialConfig}>
          <ToolBarPlugin
            editorScrollPosition={scrollTopPosition}
            onCatalogueLinkSearch={onCatalogueLinkSearch}
            catalogueSearchResponse={catalogueSearchResponse}
          />
          <div className={styles['editor__field-wrapper']}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={styles['editor__field']}
                  aria-label={t.common.rteAriaLabel}
                  aria-placeholder={placeholder}
                  title={t.common.rteTitle}
                  placeholder={<div className={styles['editor__placeholder']}>{placeholder}</div>}
                  spellCheck={false}
                  id={`${rteId}_rteEditor`}
                  style={{ height: `${maxHeightInRem}`, maxHeight: `${maxHeightInRem}` }}
                  onScroll={(event) => setScrollTopPosition(event.currentTarget.scrollTop)}
                />
              }
              placeholder={<div className={styles['editor__placeholder']}>{placeholder}</div>}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <TablePlugin />
            <TableCellResizerPlugin />
            <HistoryPlugin />
            <ListPlugin />
            <LinkPlugin validateUrl={validateUrl} />
            <AutoLinkPlugin matchers={MATCHERS} />
            <ClickableLinkPlugin />
            <OnChangePlugin onChange={handleOnChange} />
            <PasteHandlerPlugin />
            <CourseCatalogueLinkPlugin />
            {rteRef && <EditorRefPlugin editorRef={rteRef} />}
          </div>
        </LexicalComposer>
      </div>
      {notValidatedText !== '' && <FeedbackBox level="error" feedback={notValidatedText} />}
    </div>
  );
};

export const RteEditor = ({
  currentLanguage = 'NL',
  editorId,
  initialValue = '',
  placeholder = '',
  label = '',
  info = '',
  required = false,
  notValid = false,
  notValidatedText = '',
  maxWords,
  rteRef,
  onChange,
  onCatalogueLinkSearch,
  catalogueSearchResponse = { state: 'idle', results: [] },
}: RteEditorProps) => (
  <LanguageProvider>
    <Rte
      currentLanguage={currentLanguage.toUpperCase() as 'NL' | 'EN'}
      editorId={editorId}
      initialValue={initialValue}
      placeholder={placeholder}
      label={label}
      info={info}
      required={required}
      notValid={notValid}
      notValidatedText={notValidatedText}
      maxWords={maxWords}
      rteRef={rteRef}
      onChange={onChange}
      onCatalogueLinkSearch={onCatalogueLinkSearch}
      catalogueSearchResponse={catalogueSearchResponse}
    />
  </LanguageProvider>
);
